import { isRejectedWithValue } from '@reduxjs/toolkit';
import type { Middleware } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { getStoredAuthSession, isLegacyOauthSession, logoutStoredSession } from 'utils/auth/session';
import { isMobileApp } from 'utils/common';
import { recoverMobileKeycloakSession } from 'utils/mobileApp/requestAccessToken';
import {
  HELIOS_MAPPING_ERROR_CODES,
  HELIOS_MAPPING_ERROR_PARAM,
  isMappingRecoverySupportedPath,
  redirectToMappingRecovery,
} from 'utils/helios/mapping';
import { getUnauthenticatedLoginPath } from 'utils/navigation/urls';

interface ErrorDetails {
  type?: 'error' | 'validationError' | 'jwtError' | 'flowioError' | 'databaseError' | 'apiKeyError';
  title?: string;
  status?: number;
  Detail?: string;
  detail?: string;
  errorCode?: string;
  ErrorCode?: string;
  errors?: {
    [key: string]: string[];
  };
  extensions?: {
    errorCode?: string;
  };
}

interface HeliosErrorDetails {
  Type?: 'Error' | 'ValidationError' | 'JwtError' | 'FlowioError' | 'DatabaseError' | 'ApiKeyError';
  Title: string;
  Status?: number;
  Detail?: string;
  detail?: string;
  errorCode?: string;
  ErrorCode?: string;
  errors?: {
    [key: string]: string[];
  };
  extensions?: {
    errorCode?: string;
  };
  Extensions?: {
    errorCode?: string;
  };
}

interface ErrorState {
  payload: {
    status: number | 'FETCH_ERROR';
    showToast?: boolean;
    originalStatus?: number;
    data?: {
      ErrorDetails?: HeliosErrorDetails;
      errorDetails?: ErrorDetails;
      SystemType?: 'Core' | 'Flow' | 'Helios';
      systemType?: 'Core' | 'Flow' | 'Helios';
    };
  };
  meta: {
    arg?: {
      originalArgs?: unknown;
    };
    baseQueryMeta: {
      response: { url: string };
      request: Request;
    };
  };
}

const LANGUAGE_TAG_MAP: Record<string, 'cs' | 'en' | 'sk'> = {
  en: 'en',
  eng: 'en',
  cs: 'cs',
  cz: 'cs',
  sk: 'sk',
};

const normalizeHeaderLanguage = (headerValue: string | null): 'cs' | 'en' | 'sk' => {
  const firstTag = headerValue
    ?.split(',')
    .map((part) => part.trim().toLowerCase())
    .find(Boolean);

  const primaryTag = firstTag?.split('-')[0] ?? '';

  return LANGUAGE_TAG_MAP[primaryTag] ?? 'en';
};

function isNonNullObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isRtkApiError(action: unknown): action is ErrorState {
  return (
    isNonNullObject(action) &&
    isNonNullObject(action['payload']) &&
    isNonNullObject(action['meta']) &&
    isNonNullObject(action['meta']['baseQueryMeta']) &&
    isNonNullObject(action['meta']['baseQueryMeta']['request']) &&
    isNonNullObject(action['meta']['baseQueryMeta']['response'])
  );
}

function hasSuppressErrorToastFlag(action: ErrorState): boolean {
  if (!isNonNullObject(action?.meta)) {
    return false;
  }

  const arg = action?.meta?.arg;
  if (!isNonNullObject(arg)) {
    return false;
  }

  const originalArgs = arg?.originalArgs;
  if (!isNonNullObject(originalArgs)) {
    return false;
  }

  return originalArgs['suppressErrorToast'] === true;
}

const hasSuppressErrorToastForStatusFlag = (action: ErrorState, status: number | 'FETCH_ERROR') => {
  if (!isNonNullObject(action?.meta)) return false;
  const arg = action.meta.arg;
  if (!isNonNullObject(arg) || !isNonNullObject(arg.originalArgs)) return false;
  return arg.originalArgs['suppressErrorToastForStatus'] === status;
};

const RECORD_DETAIL_ARG_KEYS = new Set([
  'recordid',
  'recordnumber',
  'currentrecordnumber',
  'masterrecordid',
  'masterrecordnumber',
  'parentrecordnumber',
]);

const hasRecordDetailArg = (value: unknown): boolean =>
  isNonNullObject(value) &&
  Object.entries(value).some(([key, nested]) => RECORD_DETAIL_ARG_KEYS.has(key.toLowerCase()) || hasRecordDetailArg(nested));

const MAPPING_TEMPLATE_ARG_KEYS = new Set(['templateid', 'codelisttemplateid']);
const MAPPING_DATASOURCE_ARG_KEYS = new Set(['datasourceid']);

/**
 * First string arg matching one of the keys, preferring top-level fields over
 * nested ones (a DataTables body carries both the queried templateId and the
 * parent agenda's formData.templateId — the top-level one failed).
 */
const findArgValue = (value: unknown, keys: ReadonlySet<string>): string | undefined => {
  if (!isNonNullObject(value)) return undefined;
  for (const [key, nested] of Object.entries(value)) {
    if (keys.has(key.toLowerCase()) && typeof nested === 'string' && nested) return nested;
  }
  for (const nested of Object.values(value)) {
    const found = findArgValue(nested, keys);
    if (found) return found;
  }
  return undefined;
};

/**
 * ProblemDetails extensions serialize flattened into errorDetails (STJ
 * JsonExtensionData), but some proxies keep them nested - check both shapes
 * and both casings seen across Core/Flow/Helios gateways.
 */
const extractErrorCode = (data?: ErrorState['payload']['data']) =>
  data?.errorDetails?.errorCode ||
  data?.errorDetails?.ErrorCode ||
  data?.errorDetails?.extensions?.errorCode ||
  data?.ErrorDetails?.errorCode ||
  data?.ErrorDetails?.ErrorCode ||
  data?.ErrorDetails?.extensions?.errorCode ||
  data?.ErrorDetails?.Extensions?.errorCode;

export const rtkQueryErrorLogger: Middleware = () => (next) => (action) => {
  if (isRejectedWithValue(action) && isRtkApiError(action)) {
    const { payload, meta } = action;

    const headers = meta.baseQueryMeta.request.headers as Headers;
    const language = normalizeHeaderLanguage(headers.get('Accept-Language'));

    const errorDetails = {
      type: payload.data?.ErrorDetails?.Type || payload.data?.errorDetails?.type?.toLowerCase(),
      status: payload.data?.ErrorDetails?.Status || payload.data?.errorDetails?.status,
      detail:
        payload.data?.ErrorDetails?.Detail ||
        payload.data?.ErrorDetails?.detail ||
        payload.data?.errorDetails?.detail ||
        payload.data?.errorDetails?.Detail,
      errors: payload.data?.ErrorDetails?.errors || payload.data?.errorDetails?.errors,
      errorCode: extractErrorCode(payload.data),
    };

    const getUnknownErrorMessages = (lang: 'cs' | 'en' | 'sk') => {
      switch (lang) {
        case 'cs':
          return 'Něco se pokazilo';
        case 'en':
          return 'Something went wrong';
        case 'sk':
          return 'Niečo sa pokazilo';
        default:
          return 'Something went wrong';
      }
    };

    const getDatabaseErrorMessages = (lang: 'cs' | 'en' | 'sk') => {
      switch (lang) {
        case 'cs':
          return 'Položka, kterou se snažíte upravit nebo vymazat, není dostupná. Zkuste to znovu později.';
        case 'en':
          return 'The item you are trying to edit or delete is not available. Please try again later.';
        case 'sk':
          return 'Položka, ktorú sa snažíte upraviť alebo vymazať, nie je dostupná. Skúste to znova neskôr.';
        default:
          return 'The item you are trying to edit or delete is not available. Please try again later.';
      }
    };

    const normalizedStatus = payload.status || errorDetails.status || payload.originalStatus;

    if (normalizedStatus === 401) {
      if (meta.baseQueryMeta.response.url.includes('Refresh')) {
        logoutStoredSession();
        return;
      }

      const storedAuth = getStoredAuthSession();

      if (isMobileApp() && storedAuth?.jwtToken && !isLegacyOauthSession(storedAuth)) {
        recoverMobileKeycloakSession();
        return;
      }

      const state = {
        reason: 'unauthorized',
        errorCode: 401,
        currentLocation: window.location.href.replaceAll('&', '%26'),
      };
      window.location.href = `${getUnauthenticatedLoginPath(window.location.pathname)}?state=` + JSON.stringify(state);
      return;
    }

    if (normalizedStatus === 403) {
      return;
    }

    if (normalizedStatus === 404) {
      const requestUrl = meta.baseQueryMeta.response.url;
      if (requestUrl.includes('/api/helios/') && hasRecordDetailArg(meta.arg?.originalArgs)) {
        return next(action);
      }
      if (requestUrl.includes('/Views/') && requestUrl.includes('/Usable')) {
        return next(action);
      }
      if (requestUrl.includes('/View/') && requestUrl.includes('/Records/Light')) {
        return next(action);
      }
    }

    if (normalizedStatus === 419) {
      logoutStoredSession();
      return;
    }

    if (errorDetails.errorCode && HELIOS_MAPPING_ERROR_CODES.has(errorDetails.errorCode)) {
      const currentUrl = new URL(window.location.href);
      const alreadyRecovering = currentUrl.searchParams.has(HELIOS_MAPPING_ERROR_PARAM);
      if (!alreadyRecovering && isMappingRecoverySupportedPath(currentUrl.pathname)) {
        const result = next(action);
        redirectToMappingRecovery({
          templateId: findArgValue(meta.arg?.originalArgs, MAPPING_TEMPLATE_ARG_KEYS),
          datasourceId: findArgValue(meta.arg?.originalArgs, MAPPING_DATASOURCE_ARG_KEYS),
        });
        return result;
      }
    }

    if (hasSuppressErrorToastFlag(action) || hasSuppressErrorToastForStatusFlag(action, payload.status)) {
      return next(action);
    }

    if (errorDetails.type?.toLowerCase() === 'validationerror' || payload.data?.ErrorDetails?.Type === 'ValidationError') {
      const validationErrors: string[] = Object.values(errorDetails.errors || {}).flat();
      if (validationErrors.length > 0) {
        toast.error(validationErrors.join('\n'));
        return next(action);
      }
    }

    if (errorDetails.type?.toLowerCase() === 'databaseerror' || payload.data?.ErrorDetails?.Type === 'DatabaseError') {
      toast.error(getDatabaseErrorMessages(language));
      return next(action);
    }

    if (payload.status === 'FETCH_ERROR' || normalizedStatus === 500) {
      if (errorDetails.detail) {
        toast.error(errorDetails.detail);
      } else {
        toast.error(getUnknownErrorMessages(language));
      }
      return next(action);
    }

    if (errorDetails.detail) {
      toast.error(errorDetails.detail);
      return next(action);
    }

    toast.error(getUnknownErrorMessages(language));
  }

  return next(action);
};
